import type { InferRouterContractOutputs } from "@orpc/contract";
import { accountContract } from "./account";
import { accountActivityContract } from "./account-activity";
import { accountDefaultTemplateContract } from "./account-default-template";
import { accountProvisioningContract } from "./account-provisioning";
import { accountUsageContract } from "./account-usage";
import { accountWriteContract } from "./account-write";
import { adminContract } from "./admin";
import { audioUploadContract } from "./audio-upload";
import { billingSessionContract } from "./billing-session";
import { deviceReadContract } from "./device-read";
import { deviceWriteContract } from "./device-write";
import {
  dictatedReportContract,
  dictationReportContract,
} from "./dictation-report";
import { feedbackContract, feedbackReportContract } from "./feedback";
import { formCompletionContract } from "./form-completion";
import { gapTranscriptionContract } from "./gap-transcription";
import { macroContract } from "./macro";
import { noteEditProposalContract } from "./note-edit-proposal";
import { noteGenerationContract } from "./note-generation";
import { preChartContract } from "./pre-chart";
import { recordingCreateContract } from "./recording-create";
import { recordingInstructionsContract } from "./recording-instructions";
import { recordingSessionCreateContract } from "./recording-session-create";
import { recordingSessionReadContract } from "./recording-session-read";
import { referenceContract } from "./reference";
import { referralTemplateContract } from "./referral-template";
import { speakerRoleContract } from "./speaker-roles";
import { templateExtractionContract } from "./template-extraction";
import { templateLibraryContract } from "./template-library";
import { templateProposalContract } from "./template-proposal";
import { templateReadContract } from "./template-read";
import { templateWriteContract } from "./template-write";
import { transcriptSegmentsContract } from "./transcript-segments";
import { transcriptionRetryContract } from "./transcription-retry";
import { transcriptionRouteContract } from "./transcription-route";
import { visitContract } from "./visit";
import { visitAudioContract } from "./visit-audio";
import { visitContentContract } from "./visit-content";
import { visitReviewContract } from "./visit-review";
import { visitSourceContract } from "./visit-source";
import { visitTranscriptContract } from "./visit-transcript";
import { visitWriteContract } from "./visit-write";
import { vocabularyContract } from "./vocabulary";

export { accountContract, ownAccountSchema } from "./account";
export {
  accountDefaultTemplateContract,
  accountDefaultTemplateSchema,
  accountDefaultTemplateUpdateSchema,
  builtInTemplateSchema,
} from "./account-default-template";
export * from "./dictation-report";
export * from "./dictation-session";
export * from "./dictation-study";
export {
  EARLY_ACCESS_FEATURES,
  type EarlyAccessFeature,
  earlyAccessFeatureSchema,
  isEarlyAccessFeature,
  knownEarlyAccess,
} from "./early-access";
export { errorSchema, errorStatusMap } from "./errors";
export {
  type MacroWriteInput,
  macroContract,
  macrosSchema,
  macroWriteResultSchema,
  macroWriteSchema,
} from "./macro";
export {
  appConfigSchema,
  occupationsSchema,
  type ReferenceOutputs,
  referenceContract,
  settingsOptionsSchema,
  stripePublishableKeySchema,
  typesSchema,
} from "./reference";

export const apiContract = {
  ...adminContract,
  ...referenceContract,
  ...accountContract,
  ...accountDefaultTemplateContract,
  ...macroContract,
  ...visitContract,
  ...visitContentContract,
  ...visitWriteContract,
  ...accountUsageContract,
  ...accountActivityContract,
  ...accountWriteContract,
  ...templateReadContract,
  ...templateWriteContract,
  ...templateLibraryContract,
  ...deviceReadContract,
  ...deviceWriteContract,
  ...recordingSessionReadContract,
  ...recordingSessionCreateContract,
  ...recordingCreateContract,
  ...feedbackContract,
  ...feedbackReportContract,
  ...billingSessionContract,
  ...audioUploadContract,
  ...noteGenerationContract,
  ...noteEditProposalContract,
  ...templateProposalContract,
  ...transcriptionRetryContract,
  ...visitAudioContract,
  ...accountProvisioningContract,
  ...transcriptionRouteContract,
  ...visitSourceContract,
  ...speakerRoleContract,
  ...vocabularyContract,
  ...recordingInstructionsContract,
  ...transcriptSegmentsContract,
  ...visitTranscriptContract,
  ...visitReviewContract,
  ...referralTemplateContract,
  ...templateExtractionContract,
  ...gapTranscriptionContract,
  ...preChartContract,
  ...formCompletionContract,
  ...dictationReportContract,
  ...dictatedReportContract,
};
export type ApiOutputs = InferRouterContractOutputs<typeof apiContract>;

export {
  ACTIVITY_RANGES,
  accountActivityContract,
  type OwnActivity,
  type OwnActivityQuery,
  ownActivityQuerySchema,
  ownActivitySchema,
} from "./account-activity";
export {
  accountProfileSchema,
  passwordRegistrationSchema,
  provisionedAccountSchema,
} from "./account-provisioning";
export { accountUsageContract, ownUsageSchema } from "./account-usage";
export {
  type AccountPreferencesUpdate,
  type AccountProfileUpdate,
  accountEmailSchema,
  accountPreferencesUpdateSchema,
  accountProfileUpdateSchema,
  accountWriteContract,
} from "./account-write";
export {
  adminAuditQuerySchema,
  adminAuditSchema,
  adminContract,
  adminOverviewSchema,
  adminUserSchema,
  adminUsersQuerySchema,
  adminUsersSchema,
  decodeAdminAuditCursor,
  decodeAdminUsersCursor,
} from "./admin";
export {
  type AudioUploadInput,
  audioUploadContract,
  audioUploadCreatedSchema,
  audioUploadCreateSchema,
  audioUploadSchema,
} from "./audio-upload";
export {
  type BillingSession,
  billingSessionContract,
  billingSessionSchema,
} from "./billing-session";
export {
  deviceReadContract,
  type OwnDevicesQuery,
  ownDevicesQuerySchema,
  ownDevicesSchema,
} from "./device-read";
export {
  type DeviceRegistration,
  type DeviceUpdate,
  deviceRegistrationSchema,
  deviceUpdateSchema,
  deviceWriteContract,
  deviceWriteResultSchema,
} from "./device-write";
export * from "./doctor-note";
export {
  FEEDBACK_REPORT_HOURLY_LIMIT,
  FEEDBACK_REPORT_MAX_LENGTH,
  FEEDBACK_REPORT_TYPES,
  type FeedbackInput,
  type FeedbackReportInput,
  type FeedbackReportRequest,
  type FeedbackReportType,
  type FeedbackRequest,
  feedbackContract,
  feedbackReportContract,
  feedbackReportResultSchema,
  feedbackReportSchema,
  feedbackReportTypeSchema,
  feedbackSchema,
} from "./feedback";
export * from "./form-completion";
export * from "./gap-transcription";
export {
  combinedNoteEditInstruction,
  NOTE_EDIT_COMBINED_INSTRUCTION_MAX,
  NOTE_EDIT_INSTRUCTION_MAX,
  NOTE_EDIT_MAX_STEPS,
  type NoteEditOrigin,
  type NoteEditProposalBody,
  type NoteEditRefine,
  noteEditOriginSchema,
  noteEditProposalConflictSchema,
  noteEditProposalContract,
  noteEditProposalInputSchema,
  noteEditProposalSchema,
  noteEditRefineSchema,
} from "./note-edit-proposal";
export {
  type NoteGenerationBody,
  noteGenerationContract,
  noteGenerationSchema,
  READING_LEVEL_DEFAULT,
  READING_LEVELS,
  type ReadingLevel,
  readingLevelSchema,
  SUMMARY_LANGUAGE_DEFAULT,
  SUMMARY_LANGUAGES,
  type SummaryLanguage,
  summaryLanguageSchema,
} from "./note-generation";
export {
  isPreChartDraft,
  PRE_CHART_LAB_MODES,
  PRE_CHART_LAB_TESTS_MAX,
  PRE_CHART_STATUS,
  type PreChartCreateBody,
  type PreChartCreateInput,
  type PreChartLabMode,
  type PreChartLabs,
  type PreChartTemplateBody,
  type PreChartTemplateInput,
  preChartContract,
  preChartCreateSchema,
  preChartLabsSchema,
  preChartTemplateResultSchema,
  preChartTemplateSchema,
} from "./pre-chart";
export {
  type AnonymizationQueuePayload,
  anonymizationQueuePayloadSchema,
  type ProcessingQueuePayload,
  processingQueuePayloadSchema,
} from "./processing-queue";
export { publicIdSchema } from "./public-id";
export {
  MAX_VISIT_RECORDINGS,
  type RecordingCreateBody,
  type RecordingCreateInput,
  recordingCreateContract,
  recordingCreateSchema,
} from "./recording-create";
export {
  CUE_WINDOW_SECONDS,
  type CueShortcut,
  type DetectedCue,
  detectEndOfVisitCues,
  foldCueText,
  isInsertVerb,
} from "./recording-cues";
export {
  NOT_APPLIED_REASONS,
  RECORDING_INSTRUCTION_MAX_LENGTH,
  RECORDING_INSTRUCTIONS_MAX,
  type RecordingInstructions,
  recordingInstructionSchema,
  recordingInstructionsContract,
  recordingInstructionsSchema,
  skipShortcutsSchema,
  VISIT_INSTRUCTION_STATES,
  type VisitInstructions,
  visitInstructionsSchema,
} from "./recording-instructions";
export {
  recordingSessionCreateContract,
  recordingSessionCreateSchema,
  recordingSessionResultSchema,
} from "./recording-session-create";
export {
  type OwnRecordingSessionsQuery,
  ownRecordingSessionsQuerySchema,
  ownRecordingSessionsSchema,
  recordingSessionReadContract,
} from "./recording-session-read";
export * from "./referral-template";
export {
  applySpeakerRoles,
  CAREGIVER_RELATIONS,
  type CaregiverRelation,
  caregiverRelationSchema,
  formatSpeakerTranscript,
  hasUnidentifiedSpeakers,
  parseSpeakerTurns,
  SPEAKER_ROLE_LABELS,
  SPEAKER_ROLES,
  type SpeakerAssignment,
  type SpeakerRole,
  type SpeakerTurn,
  speakerRoleContract,
  speakerRoleSchema,
  speakerTag,
  type TranscriptSpeaker,
  transcriptSpeakers,
  type VisitSpeakers,
  type VisitSpeakerUpdate,
  visitSpeakersSchema,
  visitSpeakerUpdateSchema,
} from "./speaker-roles";
export * from "./template-extraction";
export {
  type TemplateLibraryCategoriesQuery,
  type TemplateLibraryQuery,
  templateLibraryCategoriesQuerySchema,
  templateLibraryCategoriesSchema,
  templateLibraryContract,
  templateLibraryQuerySchema,
  templateLibrarySchema,
} from "./template-library";
export {
  type TemplateProposalBody,
  type TemplateProposalDraft,
  type TemplateProposalResult,
  templateProposalContract,
  templateProposalDraftSchema,
  templateProposalInputSchema,
  templateProposalResultSchema,
  templateProposalSchema,
} from "./template-proposal";
export {
  privateTemplateListSchema,
  privateTemplateSchema,
  type TemplateListQuery,
  templateListQuerySchema,
  templateReadContract,
} from "./template-read";
export {
  type PrivateTemplateCreate,
  type PrivateTemplateDefinition,
  type PrivateTemplateUpdate,
  privateTemplateCreateSchema,
  privateTemplateDefinitionSchema,
  privateTemplateUpdateSchema,
  templateHasContent,
  templateWriteContract,
} from "./template-write";
export {
  TRANSCRIPT_SEGMENT_SOURCES,
  TRANSCRIPT_SEGMENT_STATES,
  TRANSCRIPT_SEGMENTS_MAX,
  type TranscriptSegmentItem,
  transcriptSegmentSchema,
  transcriptSegmentsContract,
  type VisitTranscriptSegments,
  visitTranscriptSegmentsSchema,
} from "./transcript-segments";
export {
  type TranscriptionRetryBody,
  transcriptionRetryContract,
  transcriptionRetryResultSchema,
  transcriptionRetrySchema,
} from "./transcription-retry";
export {
  transcriptionRouteContract,
  transcriptionRouteInputSchema,
  transcriptionRouteSchema,
} from "./transcription-route";
export {
  type TranscriptVisitCreateBody,
  type TranscriptVisitCreateInput,
  UNDETERMINED_LANGUAGE,
  type VisitCreateBody,
  type VisitCreateInput,
  type VisitListQuery,
  visitAudioSettingsSchema,
  visitContract,
  visitCreateSchema,
  visitDetailSchema,
  visitLanguageSchema,
  visitListQuerySchema,
  visitListSchema,
  visitResultSchema,
  visitSummarySchema,
  visitTranscriptCreateSchema,
} from "./visit";
export {
  type VisitAudioReplaceBody,
  visitAudioContract,
  visitAudioReplaceSchema,
  visitAudioSchema,
} from "./visit-audio";
export {
  type VisitContentQuery,
  visitContentContract,
  visitContentQuerySchema,
  visitNoteSchema,
  visitNotesSchema,
  visitRecordingSchema,
  visitRecordingsSchema,
  visitVersionSchema,
} from "./visit-content";
export {
  NOTE_SOURCE_ISSUES,
  NOTE_SOURCES_MAX_SENTENCES,
  type NoteSourceSegment,
  type NoteSourceSentence,
  noteSourceSegmentSchema,
  noteSourceSentenceSchema,
  TRANSCRIPT_FLAG_CATEGORIES,
  TRANSCRIPT_FLAG_SEVERITIES,
  TRANSCRIPT_FLAG_STATES,
  type TranscriptFlagItem,
  transcriptFlagSchema,
  type VisitNoteSources,
  type VisitTranscriptFlags,
  visitNoteSourcesSchema,
  visitReviewContract,
  visitTranscriptFlagsSchema,
} from "./visit-review";
export {
  VISIT_SOURCE_DOCX_MEDIA_TYPE,
  VISIT_SOURCE_LEGACY_MEDIA_TYPES,
  VISIT_SOURCE_MAX_BYTES,
  VISIT_SOURCE_MAX_PER_VISIT,
  VISIT_SOURCE_MEDIA_TYPES,
  VISIT_SOURCE_TEXT_MAX,
  type VisitSource,
  type VisitSourceCreateInput,
  visitSourceContract,
  visitSourceCreateSchema,
  visitSourceIdsSchema,
  visitSourceKindSchema,
  visitSourceSchema,
  visitSourceTimingSchema,
} from "./visit-source";
export {
  type VisitSummarizationEvent,
  visitSummarizationEventSchema,
} from "./visit-summarization";
export {
  TRANSCRIPT_EDIT_MAX_BYTES,
  type TranscriptCorrection,
  transcriptConflictSchema,
  transcriptCorrections,
  transcriptVersionSchema,
  type VisitTranscript,
  type VisitTranscriptUpdate,
  visitTranscriptContract,
  visitTranscriptSchema,
  visitTranscriptUpdateSchema,
} from "./visit-transcript";
export {
  type AssistantNoteReview,
  assistantNoteReviewSchema,
  assistedNoteConflictSchema,
  assistedNoteErrorSchema,
  type VisitNoteRestoreInput,
  type VisitNoteUpdateInput,
  type VisitUpdateInput,
  visitNoteRestoreConflictSchema,
  visitNoteRestoreSchema,
  visitNoteUpdateSchema,
  visitUpdateSchema,
  visitWriteContract,
} from "./visit-write";
export {
  DEEPGRAM_KEYTERM_MAX_CHARACTERS,
  DEEPGRAM_KEYTERM_MAX_TERMS,
  deepgramKeyterms,
  parseVocabularyPaste,
  TRANSCRIPTION_PROMPT_MAX_CHARACTERS,
  transcriptionPromptWithVocabulary,
  VOCABULARY_MAX_TERMS,
  VOCABULARY_SOUNDS_LIKE_MAX,
  VOCABULARY_TERM_MAX,
  type VocabularyEntry,
  type VocabularyTerm,
  type VocabularyTermInput,
  type VocabularyUpdate,
  vocabularyAddResultSchema,
  vocabularyAddSchema,
  vocabularyContract,
  vocabularyKey,
  vocabularyListSchema,
  vocabularySoundsLikeSchema,
  vocabularyTermInputSchema,
  vocabularyTermSchema,
  vocabularyTermTextSchema,
  vocabularyUpdateSchema,
} from "./vocabulary";
